import React, { useMemo, useState } from "react";
import { dayMonth } from "@/lib/dates";
import { Bug, Plus, Search, MessageSquare, Clock3, UserRound, Download, Loader2, List, KanbanSquare } from "lucide-react";
import { api, TIMEOUTS } from "@/lib/api";
import { toast } from "sonner";
import FeedbackModal from "@/components/FeedbackModal";
import FeedbackDetail from "@/components/FeedbackDetail";
import TicketBoard from "@/components/TicketBoard";
import { GROUP_OPTIONS, isMyWork } from "@/components/boardModel";
import { statusLabel, priorityLabel, quickLabel, filterLabel, appLabel } from "@/lib/labels";
import useFeedbackApps from "@/hooks/useFeedbackApps";
import {
  CategoryBadge, PriorityBadge, StatusBadge, STATUSES, PRIORITIES, AWAITING_CONFIRMATION,
  ACTIVE_STATUSES, statusOrder,
} from "@/components/FeedbackBadge";

const STATUS_FILTERS = ["All", ...STATUSES];
const PRIORITY_FILTERS = ["All", ...PRIORITIES];
const QUICK_FILTERS = ["All", "Active", "My work", "Needs my confirmation", "Recently deployed"];
const SORT_OPTIONS = [
  { value: "updated_desc", label: "Actualizate recent" },
  { value: "created_desc", label: "Cele mai noi" },
  { value: "created_asc", label: "Cele mai vechi" },
  { value: "priority_desc", label: "Prioritate" },
  { value: "status_asc", label: "Stare" },
];
const PRIORITY_RANK = { Critical: 4, High: 3, Medium: 2, Low: 1 };

function Chip({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`px-2 py-0.5 text-[11px] border transition-colors whitespace-nowrap ${
        active ? "border-brand bg-brand/10 text-brand" : "border-line/10 text-fg/60 hover:border-line/30"
      }`}
    >{children}</button>
  );
}

function safeDate(iso) {
  const ts = Date.parse(iso || "");
  return Number.isFinite(ts) ? ts : 0;
}

function timeStr(iso) {
  try { return dayMonth(iso); }
  catch { return ""; }
}

function SkeletonList() {
  return (
    <div className="space-y-2" data-testid="feedback-skeleton">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="border border-line/10 bg-surface p-3 animate-pulse">
          <div className="flex gap-1.5 mb-2">
            <div className="h-3 w-12 bg-fg/10 rounded-sm" />
            <div className="h-3 w-10 bg-fg/10 rounded-sm" />
          </div>
          <div className="h-4 w-2/3 bg-fg/10 rounded-sm mb-2" />
          <div className="h-2.5 w-1/3 bg-fg/10 rounded-sm" />
        </div>
      ))}
    </div>
  );
}

function EmptyState({ hasTickets }) {
  if (hasTickets) {
    return <div className="text-center text-fg/40 text-sm py-16">Niciun tichet nu se potrivește cu filtrele.</div>;
  }
  return (
    <div className="text-center py-20 px-6" data-testid="feedback-empty">
      <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center border border-line/10 bg-surface text-fg/30">
        <Bug className="w-7 h-7" />
      </div>
      <p className="label-tiny mb-2">// NICIUN FEEDBACK ÎNCĂ</p>
      <p className="text-sm text-fg/50 max-w-xs mx-auto">
        Încă niciun feedback. Folosește <span className="mono text-brand">/bug</span> sau <span className="text-brand">Raportează un defect</span> ca să-l trimiți pe primul.
      </p>
    </div>
  );
}

function TicketCard({ t, active, reporter, assignee, onClick }) {
  const updated = t.updated_at || t.created_at;
  const awaiting = t.status === AWAITING_CONFIRMATION;
  return (
    <button
      type="button"
      data-testid={`ticket-card-${t.id}`}
      onClick={onClick}
      className={`w-full text-left border p-3 transition-colors ${
        active ? "border-brand bg-brand/5" : "border-line/10 bg-surface hover:border-line/30"
      }`}
      style={awaiting ? {
        borderColor: "rgb(var(--agent-needs-review))",
        backgroundColor: "rgb(var(--agent-needs-review) / 0.05)",
      } : undefined}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
          <StatusBadge value={t.status} custom={t.custom_status} />
          <PriorityBadge value={t.priority} />
          <CategoryBadge value={t.category} />
        </div>
        <span className="shrink-0 inline-flex items-center gap-1 text-[10px] text-fg/45 mono" title={`Actualizat ${updated}`}>
          <Clock3 className="w-3 h-3" /> {timeStr(updated)}
        </span>
      </div>
      <p className="text-sm font-semibold text-fg break-words leading-snug">{t.title}</p>
      <div className="mt-2 grid gap-1 text-[11px] text-fg/50 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
        <p className="min-w-0 truncate">
          <span className="mono text-fg/65">{t.app}</span>
          <span className="mx-1.5 text-fg/25">•</span>
          <span>{reporter}</span>
          <span className="mx-1.5 text-fg/25">•</span>
          <span>creat {timeStr(t.created_at)}</span>
        </p>
        <div className="flex items-center gap-2 min-w-0 sm:justify-end">
          {assignee ? (
            <span className="inline-flex items-center gap-1 min-w-0 text-[10px] text-fg/55">
              <UserRound className="w-3 h-3 shrink-0" />
              <span className="truncate max-w-[160px]">{assignee}</span>
            </span>
          ) : <span className="text-[10px] text-fg/35">Nealocat</span>}
          {t.replies?.length > 0 && (
            <span className="text-[10px] text-fg/50 flex items-center gap-0.5 shrink-0">
              <MessageSquare className="w-3 h-3" /> {t.replies.length}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

export default function FeedbackView({
  workspaceId, currentUser, users, channels = [], projects = [], isOwner, myRole = "member", tickets, loading,
  onUpsert, detailId, onOpenDetail, onCloseDetail, onGoToChannel,
}) {
  const canExport = myRole === "owner" || myRole === "admin";
  const [exporting, setExporting] = useState("");
  const [appFilter, setAppFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [priorityFilter, setPriorityFilter] = useState("All");
  const [reporterFilter, setReporterFilter] = useState("All");
  const [assigneeFilter, setAssigneeFilter] = useState("All");
  const [quickFilter, setQuickFilter] = useState("All");
  const [sortBy, setSortBy] = useState("updated_desc");
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  // Board preferences are a per-person habit, not workspace data: remembered
  // locally, like the Atena model choice.
  const pref = (k, d) => { try { return localStorage.getItem(k) ?? d; } catch { return d; } };
  const remember = (k, v) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } };
  const [view, setViewState] = useState(() => pref("vs_fb_view", "list"));
  const [groupBy, setGroupByState] = useState(() => pref("vs_fb_group", "none"));
  const [showDone, setShowDoneState] = useState(() => pref("vs_fb_done", "1") === "1");
  const setView = (v) => { setViewState(v); remember("vs_fb_view", v); };
  const setGroupBy = (v) => { setGroupByState(v); remember("vs_fb_group", v); };
  const setShowDone = (v) => { setShowDoneState(v); remember("vs_fb_done", v ? "1" : "0"); };
  const projectName = (id) => projects.find((p) => p.id === id)?.name || null;

  // A move on the board is the same PATCH the detail panel makes. Optimistic,
  // because the card has visibly landed; reverted with the server's reason if
  // it refuses (a member may only resolve their own report, say).
  const moveTicket = async (t, status) => {
    onUpsert({ ...t, status, custom_status: null });
    try {
      const { data } = await api.patch(`/feedback/${t.id}`, { status, custom_status: null });
      onUpsert(data);
    } catch (e) {
      onUpsert(t);
      toast.error(e?.response?.data?.detail || "Mutarea a eșuat");
    }
  };

  // The export mirrors what is on screen: exporting a filtered view and getting
  // the whole tracker back would be a quiet way to hand over more than intended.
  const runExport = async (format) => {
    setExporting(format);
    try {
      const params = { workspace_id: workspaceId, format };
      if (appFilter !== "All") params.app = appFilter;
      if (priorityFilter !== "All") params.priority = priorityFilter;
      if (statusFilter !== "All") params.status = statusFilter;
      const res = await api.get("/feedback/export",
        { params, responseType: "blob", timeout: TIMEOUTS.export });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `feedback-export-${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Exportul ${format.toUpperCase()} a eșuat`);
    } finally { setExporting(""); }
  };

  const nameOf = (id) => {
    if (!id) return null;
    if (id.startsWith("bot:")) return `🤖 ${id.slice(4)}`;  // agent-assigned
    if (id === currentUser.id) return currentUser.name;
    return users.find((u) => u.id === id)?.name || "Necunoscut";
  };

  // Lista permisă vine de la server; filtrul arată însă și valorile pe care le
  // poartă tichete vechi, marcate — altfel un tichet ar deveni de negăsit.
  const { apps: allowedApps } = useFeedbackApps(workspaceId);
  const knownApps = useMemo(() => new Set(allowedApps), [allowedApps]);

  const apps = useMemo(
    () => ["All", ...Array.from(new Set(tickets.map((t) => t.app).filter(Boolean))).sort((a, b) => a.localeCompare(b))],
    [tickets]);

  const reporterOptions = useMemo(() => {
    const names = new Map([
      [currentUser.id, currentUser.name],
      ...users.map((user) => [user.id, user.name]),
    ]);
    return Array.from(new Set(tickets.map((ticket) => ticket.reporter_id).filter(Boolean)))
      .map((id) => ({ id, name: names.get(id) || "Necunoscut" }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [tickets, currentUser.id, currentUser.name, users]);

  const assigneeOptions = useMemo(() => {
    const seen = new Set();
    return tickets
      .map((ticket) => ticket.assignee_id)
      .filter(Boolean)
      .filter((id) => { if (seen.has(id)) return false; seen.add(id); return true; })
      .map((id) => ({ id, name: nameOf(id) || "Necunoscut" }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [tickets, currentUser.id, currentUser.name, users]);

  const matchesStatus = (t) =>
    statusFilter === "All" ||
    (statusFilter === AWAITING_CONFIRMATION
      ? t.status === AWAITING_CONFIRMATION
      : t.status === statusFilter);

  const matchesQuickFilter = (t) => {
    if (quickFilter === "All") return true;
    if (quickFilter === "Active") return ACTIVE_STATUSES.has(t.status);
    if (quickFilter === "My work") return isMyWork(t, currentUser.id);
    if (quickFilter === "Recently deployed") return t.status === "Fix Deployed" || t.status === AWAITING_CONFIRMATION;
    if (quickFilter === "Needs my confirmation") {
      return t.status === AWAITING_CONFIRMATION && (isOwner || t.reporter_id === currentUser.id);
    }
    return true;
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = tickets.filter((t) =>
      (appFilter === "All" || t.app === appFilter) &&
      (reporterFilter === "All" || t.reporter_id === reporterFilter) &&
      (assigneeFilter === "All" ||
        (assigneeFilter === "__unassigned" ? !t.assignee_id : t.assignee_id === assigneeFilter)) &&
      matchesStatus(t) &&
      matchesQuickFilter(t) &&
      (priorityFilter === "All" || t.priority === priorityFilter) &&
      (!q || [t.title, t.app, nameOf(t.reporter_id), nameOf(t.assignee_id)].some((v) => (v || "").toLowerCase().includes(q)))
    );
    return [...list].sort((a, b) => {
      if (sortBy === "created_asc") return safeDate(a.created_at) - safeDate(b.created_at);
      if (sortBy === "created_desc") return safeDate(b.created_at) - safeDate(a.created_at);
      if (sortBy === "priority_desc") return (PRIORITY_RANK[b.priority] || 0) - (PRIORITY_RANK[a.priority] || 0) || safeDate(b.updated_at) - safeDate(a.updated_at);
      if (sortBy === "status_asc") return (statusOrder(a.status) - statusOrder(b.status)) || safeDate(b.updated_at) - safeDate(a.updated_at);
      return safeDate(b.updated_at || b.created_at) - safeDate(a.updated_at || a.created_at);
    });
  }, [tickets, appFilter, reporterFilter, assigneeFilter, statusFilter, quickFilter, priorityFilter, search, sortBy, isOwner, currentUser.id, users]);

  const summary = useMemo(() => ({
    total: tickets.length,
    visible: filtered.length,
    active: tickets.filter((t) => ACTIVE_STATUSES.has(t.status)).length,
    awaiting: tickets.filter((t) => t.status === AWAITING_CONFIRMATION && (isOwner || t.reporter_id === currentUser.id)).length,
    deployed: tickets.filter((t) => t.status === "Fix Deployed" || t.status === AWAITING_CONFIRMATION).length,
  }), [tickets, filtered.length, isOwner, currentUser.id]);

  const resetFilters = () => {
    setAppFilter("All");
    setStatusFilter("All");
    setPriorityFilter("All");
    setReporterFilter("All");
    setAssigneeFilter("All");
    setQuickFilter("All");
    setSortBy("updated_desc");
    setSearch("");
  };

  const detail = detailId ? tickets.find((t) => t.id === detailId) : null;

  return (
    <div className="h-full flex min-h-0">
      <div className="flex-1 flex flex-col min-h-0">
        <div className="min-h-14 px-4 sm:px-6 py-2 flex items-center gap-3 border-b border-line/10 bg-bg flex-wrap">
          <Bug className="w-4 h-4 text-brand" />
          <h2 className="font-bold tracking-tight">Registru de feedback</h2>
          <span className="text-fg/40 text-xs">{summary.visible}/{summary.total}</span>
          <div className="ml-auto flex items-center gap-2 flex-wrap justify-end">
            <span className="hidden md:inline text-[11px] text-fg/45 mono">active {summary.active} · așteaptă {summary.awaiting} · livrate {summary.deployed}</span>
            <div className="flex items-center border border-line/20" role="group" aria-label="Vizualizare">
              <button type="button" data-testid="view-list" onClick={() => setView("list")} title="Listă" aria-pressed={view === "list"}
                className={`px-2 py-1.5 ${view === "list" ? "bg-brand/15 text-brand" : "text-fg/60 hover:text-fg"}`}>
                <List className="w-3.5 h-3.5" />
              </button>
              <button type="button" data-testid="view-board" onClick={() => setView("board")} title="Board" aria-pressed={view === "board"}
                className={`px-2 py-1.5 border-l border-line/20 ${view === "board" ? "bg-brand/15 text-brand" : "text-fg/60 hover:text-fg"}`}>
                <KanbanSquare className="w-3.5 h-3.5" />
              </button>
            </div>
            {view === "board" && (
              <>
                <select data-testid="board-group" aria-label="Grupează după" value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="input-flat text-xs py-1">
                  {GROUP_OPTIONS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                </select>
                <button type="button" data-testid="board-toggle-done" onClick={() => setShowDone(!showDone)} aria-pressed={showDone}
                  className={`text-xs px-2 py-1.5 border ${showDone ? "border-line/20 text-fg/60" : "border-brand/40 text-brand"}`}>
                  {showDone ? "Ascunde finalizate" : "Arată finalizate"}
                </button>
              </>
            )}
            {canExport && ["csv", "pdf"].map((format) => (
              <button
                key={format}
                type="button"
                data-testid={`export-${format}`}
                onClick={() => runExport(format)}
                disabled={Boolean(exporting)}
                title={`Exportă vizualizarea curentă ca ${format.toUpperCase()}`}
                className="text-sm flex items-center gap-1.5 px-3 py-1.5 border border-line/20 text-fg/70 hover:text-fg hover:border-line/40 disabled:opacity-50 transition-colors"
              >
                {exporting === format
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Download className="w-3.5 h-3.5" />}
                {format.toUpperCase()}
              </button>
            ))}
            <button data-testid="new-report" onClick={() => setModalOpen(true)} className="btn-primary text-sm flex items-center gap-1.5 px-3 py-1.5">
              <Plus className="w-3.5 h-3.5" /> Raport nou
            </button>
          </div>
        </div>

        <div className="px-4 sm:px-6 py-3 border-b border-line/10 space-y-2 bg-bg/95">
          <div className="flex items-center gap-2 flex-wrap">
            <select data-testid="app-filter" aria-label="Filtrează după aplicație" value={appFilter} onChange={(e) => setAppFilter(e.target.value)} className="input-flat text-xs py-1 max-w-[200px]">
              {apps.map((a) => <option key={a} value={a}>{a === "All" ? "Toate" : appLabel(a, knownApps.size === 0 || knownApps.has(a))}</option>)}
            </select>
            <select
              data-testid="reporter-filter"
              aria-label="Filtrează după raportor"
              value={reporterFilter}
              onChange={(e) => setReporterFilter(e.target.value)}
              className="input-flat text-xs py-1 max-w-[200px]"
            >
              <option value="All">Toți raportorii</option>
              <option value={currentUser.id}>Tichetele mele</option>
              {reporterOptions
                .filter((reporter) => reporter.id !== currentUser.id)
                .map((reporter) => <option key={reporter.id} value={reporter.id}>{reporter.name}</option>)}
            </select>
            <select
              data-testid="assignee-filter"
              aria-label="Filtrează după responsabil"
              value={assigneeFilter}
              onChange={(e) => setAssigneeFilter(e.target.value)}
              className="input-flat text-xs py-1 max-w-[200px]"
            >
              <option value="All">Toți responsabilii</option>
              <option value="__unassigned">Nealocat</option>
              {assigneeOptions.map((assignee) => <option key={assignee.id} value={assignee.id}>{assignee.name}</option>)}
            </select>
            <select
              data-testid="sort-filter"
              aria-label="Ordonează tichetele"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="input-flat text-xs py-1 max-w-[190px]"
            >
              {SORT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            <div className="relative flex-1 min-w-[180px]">
              <Search className="w-3.5 h-3.5 text-fg/40 absolute left-2 top-1/2 -translate-y-1/2" />
              <input data-testid="feedback-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Caută titlu, aplicație, raportor, responsabil…" className="input-flat text-xs py-1 pl-7 w-full" />
            </div>
            <button type="button" onClick={resetFilters} className="text-[11px] text-fg/45 hover:text-fg px-2 py-1">Resetează</button>
          </div>
          <div className="flex items-center gap-1 flex-wrap" aria-label="Filtre rapide">
            {QUICK_FILTERS.map((f) => <Chip key={f} testId={`quick-filter-${f}`} active={quickFilter === f} onClick={() => setQuickFilter(f)}>{quickLabel(f)}</Chip>)}
          </div>
          <div className="flex items-center gap-1 flex-wrap" aria-label="Filtre de stare">
            {STATUS_FILTERS.map((s) => <Chip key={s} active={statusFilter === s} onClick={() => setStatusFilter(s)}>{filterLabel(s, statusLabel)}</Chip>)}
          </div>
          <div className="flex items-center gap-1 flex-wrap" aria-label="Filtre de prioritate">
            {PRIORITY_FILTERS.map((p) => <Chip key={p} active={priorityFilter === p} onClick={() => setPriorityFilter(p)}>{filterLabel(p, priorityLabel)}</Chip>)}
          </div>
        </div>

        {view === "board" ? (
          loading && tickets.length === 0 ? <div className="p-4"><SkeletonList /></div> : (
            <TicketBoard
              tickets={filtered} groupBy={groupBy} showDone={showDone}
              nameOf={nameOf} projectName={projectName} detailId={detailId}
              onOpen={onOpenDetail} onMove={moveTicket}
            />
          )
        ) : (
        <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="feedback-list">
          {loading && tickets.length === 0 ? (
            <SkeletonList />
          ) : filtered.length === 0 ? (
            <EmptyState hasTickets={tickets.length > 0} />
          ) : filtered.map((t) => (
            <TicketCard
              key={t.id} t={t} active={detailId === t.id}
              reporter={nameOf(t.reporter_id)} assignee={nameOf(t.assignee_id)}
              onClick={() => onOpenDetail(t.id)}
            />
          ))}
        </div>
        )}
      </div>

      {detail && (
        <FeedbackDetail
          // Remount on ticket switch: the panel seeds local drafts (title, app,
          // description, reply) from the ticket at mount, so reusing the
          // instance would leave the previous ticket's drafts on screen.
          key={detail.id}
          ticket={detail} currentUser={currentUser} users={users} channels={channels} isOwner={isOwner}
          nameOf={nameOf} onClose={onCloseDetail} onUpsert={onUpsert} onGoToChannel={onGoToChannel}
        />
      )}

      {modalOpen && (
        <FeedbackModal workspaceId={workspaceId} onClose={() => setModalOpen(false)} onCreated={onUpsert} />
      )}
    </div>
  );
}
